import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import classNames from "classnames";
import ActivityService from "../../../Services/Activity.service";
import useService from "../../Hooks/useService";
import ServiceResult from "../../UI/ServiceResult/ServiceResult";
import ActivityFeedContent from "./ActivityFeedContent";
import FAIcon from "../../UI/FAIcon/FAIcon";
import { AlertError, AlertWarning, } from "../../UI/StandardAlerts/StandardAlerts";
import Loading from "../../UI/Loading/Loading";
import SetService from "../../../Services/Shared/Set.service";
import usePoll from "../../Hooks/usePoll";
import ActivityItemTypeService from "../../../Domain/ActivityItemType/ActivityItemType.service";
import NoteService from "../../../Services/Note.service";
import MessageService from "../../../Services/Message.service";
const getEditableItems = (items) => {
    if (!items) {
        return Promise.resolve([]);
    }
    const promises = [];
    // Notes
    const notes = items.filter((item) => item.activityItemType.id === ActivityItemTypeService.NoteId);
    if (notes.length > 0) {
        promises.push(NoteService.canIEdit(notes.map((item) => item.itemIdInt)).then((editableNotes) => Object.keys(editableNotes).map((noteId) => ({
            id: noteId,
            activityItemTypeId: ActivityItemTypeService.NoteId,
            editableUntil: editableNotes[noteId] > -1
                ? new Date(editableNotes[noteId] + new Date().getTime())
                : true,
        }))));
    }
    // Messages
    const messages = items.filter((item) => item.activityItemType.id === ActivityItemTypeService.Message);
    if (messages.length > 0) {
        promises.push(MessageService.canIUnlog(messages.map((item) => item.itemIdUUID)).then((unloggableMessages) => Object.keys(unloggableMessages).map((messageId) => ({
            id: messageId,
            activityItemTypeId: ActivityItemTypeService.Message,
            editableUntil: unloggableMessages[messageId] > -1
                ? new Date(unloggableMessages[messageId] + new Date().getTime())
                : true,
        }))));
    }
    return Promise.all(promises).then((results) => results.flat());
};
const DEFAULT_PAGE_SIZE = 20;
const ActivityFeed = (props) => {
    const [activityState, loadActivity, activityStateActions] = useService(ActivityService.searchActivityItems);
    const [showLoadMoreButton, setShowLoadMoreButton] = React.useState(false);
    // Poll for updates to the feed
    const lastId = React.useRef(null);
    const [shouldRefresh, setShouldRefresh] = React.useState(false);
    const [highlightRefreshButton, setHighlightRefreshButton] = React.useState(false);
    const { startPoll, stopPoll } = usePoll(React.useCallback(() => {
        if (!props.poll) {
            return Promise.reject(null);
        }
        // @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
        return props.poll().then((id) => {
            if (id && lastId.current !== id) {
                setShouldRefresh(true);
            }
            lastId.current = id;
        });
    }, [props]), {
        startPollingImmediately: false,
        timeMs: 5000,
    })[1];
    const [editableItems, setEditableItems] = React.useState([]);
    const itemsContainer = React.useRef();
    React.useEffect(() => {
        if (shouldRefresh && props.search) {
            const search = props.search; // avoid type invalidation
            setShouldRefresh(false);
            ActivityService.searchActivityItems(search, {
                max: props.pageSize || DEFAULT_PAGE_SIZE,
                offset: 0,
            }).then((results) => {
                if (itemsContainer.current &&
                    // @ts-expect-error - TS2339 - Property 'contains' does not exist on type 'never'.
                    itemsContainer.current.contains(document.activeElement)) {
                    // Highlight refresh button
                    stopPoll();
                    setHighlightRefreshButton(true);
                }
                else {
                    // We need to merge these results with the current state.
                    activityStateActions.edit((currentState) => {
                        if (!currentState) {
                            return results;
                        }
                        // This is a little crude, but it works.  We may want to revisit this.
                        // @ts-expect-error - TS7006 - Parameter 'a' implicitly has an 'any' type. | TS7006 - Parameter 'b' implicitly has an 'any' type.
                        const isAboutSameItem = (a, b) => a.activityItem.activityItemType.id ===
                            b.activityItem.activityItemType.id &&
                            a.activityItem.itemIdInt === b.activityItem.itemIdInt &&
                            a.activityItem.itemIdUUID === b.activityItem.itemIdUUID;
                        const overriddenItems = currentState.items.map((oldItem) => (results === null || results === void 0 ? void 0 : results.items.find((newItem) => isAboutSameItem(oldItem, newItem))) || oldItem);
                        const newItems = ((results === null || results === void 0 ? void 0 : results.items) || []).filter((newItem) => !currentState.items.some((oldItem) => isAboutSameItem(newItem, oldItem)));
                        getEditableItems(newItems.map((item) => item.activityItem)).then((editItems) => setEditableItems((ei) => ei.concat(editItems)));
                        return Object.assign(Object.assign({}, currentState), { 
                            // Assume that new items are the latest items.
                            items: [...newItems, ...overriddenItems] });
                    });
                }
            });
        }
    }, [
        activityStateActions,
        props.pageSize,
        props.search,
        shouldRefresh,
        stopPoll,
    ]);
    const refresh = React.useCallback((search, pageSize) => {
        stopPoll();
        if (search) {
            loadActivity(search, {
                max: pageSize,
            }).then((results) => {
                var _a;
                setShowLoadMoreButton(((_a = results === null || results === void 0 ? void 0 : results.items) === null || _a === void 0 ? void 0 : _a.length) === pageSize);
                lastId.current = results.waterMark;
                startPoll();
                getEditableItems(results.items.map((item) => item.activityItem)).then((editItems) => setEditableItems((ei) => ei.concat(editItems)));
            });
        }
    }, [loadActivity, startPoll, stopPoll]);
    React.useEffect(() => {
        if (props.search) {
            refresh(props.search, props.pageSize || DEFAULT_PAGE_SIZE);
        }
    }, [props.pageSize, props.search, refresh]);
    const [loadMoreState, loadMore] = useService(React.useCallback(() => {
        // get the offset
        const nextOffset = activityState.data && activityState.data.nextOffset;
        // If there is no last date (if we don't have the data for some reason, resolve with nothing)
        if (!nextOffset) {
            return Promise.resolve(null);
        }
        if (!props.search) {
            return Promise.resolve(null);
        }
        // Perform the same search, but sent the eventDate to the last date.
        // We don't have to worry about if there's an eventDateTo in the search, it will always
        // be less than or equal to the last date.
        return ActivityService.searchActivityItems(Object.assign(Object.assign({}, props.search), { epochMilliOffset: nextOffset }), { max: props.pageSize || DEFAULT_PAGE_SIZE }).then((results) => {
            var _a, _b, _c, _d, _e;
            // We need to merge together the results, as there could be an overlap if there were new events created
            // since the feed was refreshed.
            const newItems = SetService.unionArray(((_a = activityState.data) === null || _a === void 0 ? void 0 : _a.items) || [], results === null || results === void 0 ? void 0 : results.items, (a, b) => a.activityItem.activityItemType.id ===
                b.activityItem.activityItemType.id &&
                a.activityItem.itemIdInt === b.activityItem.itemIdInt &&
                a.activityItem.itemIdUUID === b.activityItem.itemIdUUID);
            // If we haven't loaded any new items, assume they are no new items (this is an assumption, and not always true,
            // but will be true almost always for sufficiently large page sizes) hide the loadMore button.
            // @ts-expect-error - TS2339 - Property 'length' does not exist on type '{ activityItem: ActivityItem; updates: ItemUpdate[]; }[] | { activityItem: ActivityItem; updates: ItemUpdate[]; }'.
            if (newItems.length === ((_c = (_b = activityState.data) === null || _b === void 0 ? void 0 : _b.items) === null || _c === void 0 ? void 0 : _c.length)) {
                setShowLoadMoreButton(false);
            }
            // Check to see if the newest items are editable
            const justAddedItems = 
            // @ts-expect-error - TS2339 - Property 'slice' does not exist on type '{ activityItem: ActivityItem; updates: ItemUpdate[]; }[] | { activityItem: ActivityItem; updates: ItemUpdate[]; }'.
            newItems.slice(((_e = (_d = activityState.data) === null || _d === void 0 ? void 0 : _d.items) === null || _e === void 0 ? void 0 : _e.length) || 0) || [];
            getEditableItems(
            // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
            justAddedItems.map((item) => item.activityItem)).then((editItems) => setEditableItems((ei) => ei.concat(editItems)));
            // Edit the itemUpdates with the new updates.
            activityStateActions.edit(
            // @ts-expect-error - TS2345 - Argument of type '(updates: { items: { activityItem: ActivityItem; updates: ItemUpdate[]; }[]; nextOffset: number | null | undefined; waterMark: number | null | undefined; } | null | undefined) => { ...; } | ... 1 more ... | undefined' is not assignable to parameter of type '{ items: { activityItem: ActivityItem; updates: ItemUpdate[]; }[]; nextOffset: number | null | undefined; waterMark: number | null | undefined; } | ((arg1?: { items: { ...; }[]; nextOffset: number | ... 1 more ... | undefined; waterMark: number | ... 1 more ... | undefined; } | null | undefined) => { ...; } | ... 1 ...'.
            (updates) => updates && Object.assign(Object.assign({}, updates), { nextOffset: results.nextOffset, items: newItems }));
            return results;
        });
    }, [
        activityState.data,
        activityStateActions,
        props.pageSize,
        props.search,
    ]));
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "mb-2", children: !props.hideFilterComponent && (_jsxs("div", { className: "py-2 px-3 d-flex justify-content-end", children: [props.filterComponent && (_jsx("div", { className: "mr-2", children: props.filterComponent })), _jsx("button", { className: classNames("btn mr-2 empty-btn-link", {
                                "btn-primary": highlightRefreshButton,
                                "btn-link": !highlightRefreshButton,
                            }), title: highlightRefreshButton
                                ? "New items detected, click to refresh"
                                : "Refresh feed", onClick: () => {
                                if (props.search) {
                                    refresh(props.search, props.pageSize || DEFAULT_PAGE_SIZE);
                                }
                            }, disabled: activityState.isLoading, children: _jsx(FAIcon, { icon: "sync", srText: highlightRefreshButton
                                    ? "New items detected, click to refresh"
                                    : "Refresh feed", isLoading: activityState.isLoading }) })] })) }), _jsx(ServiceResult, { state: activityState, children: (activity) => activity.items.length ? (_jsxs(_Fragment, { children: [_jsx(ActivityFeedContent, { feed: activity.items, setFeed: activityStateActions.edit, editableItems: editableItems, hideItemEventTypes: props.hideItemEventTypes }), showLoadMoreButton && (_jsxs(_Fragment, { children: [loadMoreState.hasError && (_jsx(AlertError, { message: "There was an error fetching additional activity", detail: loadMoreState.errorMessage })), _jsx("div", { className: "d-flex justify-content-center", children: _jsxs("button", { className: "btn btn-outline-secondary", disabled: loadMoreState.isLoading, onClick: () => loadMore(), children: [loadMoreState.isLoading && _jsx(Loading, { addMarginRight: true }), "More"] }) })] }))] })) : (_jsx(AlertWarning, { message: "No activity found" })) })] }));
};
export default ActivityFeed;
